import React, { useState } from "react";
import { useSelector } from "react-redux";
import useApi from "../../../CustomHooks/useApi";
import useRDService from "../../../CustomHooks/useRDService";
import { Fingerprint, ArrowLeft, CheckCircle, XCircle } from "lucide-react";
import $ from "jquery";

export default function SubhearnTwoFactorAuth({ onBack, onSuccess }) {
  const merchantdetails = useSelector((s) => s.auth?.employeeDetails);
  const username = useSelector((s) => s.auth?.employeeDetails?.username);
  const { lat, lng }    = useSelector((s) => s.location);

  const userId    = merchantdetails?.username;
  const mid    = username;
  const latitude  = lat?.toString() || "";
  const longitude = lng?.toString() || "";

  const { captureFingerprint, loading: rdLoading } = useRDService();

  const { callApi: verifyTfaApi } = useApi("/aeps/subhearn/bank1/verify-tfa", "POST");

  const [captureLoading, setCaptureLoading] = useState(false);
  const [popup, setPopup] = useState({ show: false, type: "success", message: "" });

  const parsePidXml = (xml) => {
    const $xml = $(xml);
    const get = (sel, attr) => $xml.find(sel).attr(attr) || "0";
    const infoValue = (name) =>
      $xml.find(`Info[name='${name}']`).attr("value") ||
      $xml.find(`Param[name='${name}']`).attr("value") || "0";
    return {
      srno:        infoValue("srno"),
      errCode:     get("Resp", "errCode"),
      errInfo:     get("Resp", "errInfo"),
      fCount:      get("Resp", "fCount"),
      fType:       get("Resp", "fType"),
      nmPoints:    get("Resp", "nmPoints"),
      qScore:      get("Resp", "qScore"),
      dpId:        get("DeviceInfo", "dpId"),
      rdsId:       get("DeviceInfo", "rdsId"),
      rdsVer:      get("DeviceInfo", "rdsVer"),
      dc:          get("DeviceInfo", "dc"),
      mi:          get("DeviceInfo", "mi"),
      mc:          get("DeviceInfo", "mc"),
      ci:          get("Skey", "ci"),
      sessionKey:  $xml.find("Skey").text() || "0",
      hmac:        $xml.find("Hmac").text()  || "0",
      pidDataType: get("Data", "type"),
      pidData:     $xml.find("Data").text()  || "0",
    };
  };

  const handle2FALogin = async () => {
    try {
      setCaptureLoading(true);

      const pidXml = await captureFingerprint("");
      if (!pidXml || !pidXml.includes('errCode="0"')) {
        setPopup({ show: true, type: "error", message: "Fingerprint capture failed on device. Please try again." });
        return;
      }

      const pidData       = parsePidXml(pidXml);
      const aadhaarNumber = merchantdetails?.aadhaar_number || "";
      const phone           = merchantdetails?.mobile_number  || "";

      const response = await verifyTfaApi({
        mid,
        latitude,
        longitude,
        biometricData: pidData,
      });

      const success =
        response?.status === true ||
        response?.statuscode === "TXN" ||
        (typeof response?.message === "string" && response.message.toLowerCase().includes("success"));

      if (success) {
        setPopup({ show: true, type: "success", message: response?.message || "Daily 2FA Login Successful" });
        if (onSuccess) {
          setTimeout(() => {
            setPopup({ show: false, type: "success", message: "" });
            onSuccess();
          }, 2000);
        }
      } else {
        setPopup({ show: true, type: "error", message: response?.message || "Authentication failed. Please retry." });
      }
    } catch (err) {
      setPopup({
        show: true, type: "error",
        message: err?.response?.data?.message || err?.message || "Something went wrong.",
      });
    } finally {
      setCaptureLoading(false);
    }
  };

  const busy = captureLoading || rdLoading;

  return (
    <div className="bg-[#F5F7FB] px-6 py-10 min-h-screen relative">
      <div className="max-w-md mx-auto">
        <div className="bg-white rounded-3xl shadow-xl p-8">
          <button
            onClick={onBack}
            className="flex items-center gap-2 mb-8 text-gray-600 hover:text-blue-600 transition-colors"
          >
            <ArrowLeft size={18} />
            Back
          </button>

          <div className="w-24 h-24 rounded-full bg-blue-50 flex items-center justify-center mx-auto mb-6">
            <Fingerprint size={50} className={`text-blue-600 ${busy ? "animate-pulse" : ""}`} />
          </div>

          <h2 className="text-2xl font-semibold text-center mb-2 text-gray-800">
            Daily 2FA
          </h2>
          <p className="text-gray-500 text-center mb-8 text-sm">
            {busy ? "Waiting for device scan…" : "Capture fingerprint for daily session login"}
          </p>

          <button
            onClick={handle2FALogin}
            disabled={busy}
            className="w-full bg-blue-600 text-white py-4 rounded-xl hover:bg-blue-700 font-semibold transition-all disabled:opacity-60 disabled:cursor-not-allowed"
          >
            {busy ? "Processing…" : "Capture Fingerprint"}
          </button>
        </div>
      </div>

      {/* Result popup */}
      {popup.show && (
        <div className="fixed inset-0 z-[999] flex items-center justify-center bg-black/40 backdrop-blur-sm px-4">
          <div className="bg-white rounded-2xl shadow-2xl p-6 w-full max-w-sm text-center">
            <div className="flex justify-center mb-4">
              {popup.type === "success"
                ? <CheckCircle size={56} className="text-green-500" />
                : <XCircle    size={56} className="text-red-500"   />}
            </div>
            <h3 className={`text-2xl font-bold mb-3 ${popup.type === "success" ? "text-green-600" : "text-red-600"}`}>
              {popup.type === "success" ? "Success" : "Failed"}
            </h3>
            <p className="text-gray-600 mb-8 font-medium leading-relaxed">{popup.message}</p>
            <button
              onClick={() => setPopup({ show: false, type: "success", message: "" })}
              className={`w-full py-3.5 rounded-xl font-semibold text-white transition-colors
                ${popup.type === "success" ? "bg-green-600 hover:bg-green-700" : "bg-red-600 hover:bg-red-700"}`}
            >
              Okay
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
